import React, {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import { toast } from "sonner";
import { CommanServices } from "@/services/commanServices";
import {
    clearDutyCheckJob,
    isJobStale,
    loadDutyCheckJob,
    saveDutyCheckJob,
} from "@/services/dutyCheckJobStorage";
import {
    getInflightDutyCheckPromise,
    recoverDutyCheckJob,
    runDutyCheckRequest,
} from "@/services/dutyCheckJobRunner";

const DutyCheckJobContext = createContext(null);

const COMPLETE_EVENT = "dutyCheckJobComplete";

let lastToastedRequestId = null;

function emitComplete(detail) {
    window.dispatchEvent(new CustomEvent(COMPLETE_EVENT, { detail }));
}

export function DutyCheckJobProvider({ children }) {
    const [job, setJob] = useState(() => loadDutyCheckJob());
    const [modalOpen, setModalOpen] = useState(false);
    const [departments, setDepartments] = useState([]);
    const [appUsers, setAppUsers] = useState([]);
    const recoveryStarted = useRef(false);
    const completingRef = useRef(false);

    const showSuccessToast = useCallback((message, requestId) => {
        if (requestId && lastToastedRequestId === requestId) return;
        lastToastedRequestId = requestId || null;
        toast.success(message || "Duty check initiated!");
    }, []);

    const showErrorToast = useCallback((message, requestId) => {
        if (requestId && lastToastedRequestId === requestId) return;
        lastToastedRequestId = requestId || null;
        toast.error(message || "Failed to initiate duty check");
    }, []);

    const finishJob = useCallback(
        ({ success, message, requestId, notifyToast = true }) => {
            if (completingRef.current) return;
            completingRef.current = true;

            clearDutyCheckJob();
            setJob(null);
            setModalOpen(false);

            if (notifyToast) {
                if (success) {
                    showSuccessToast(message, requestId);
                } else {
                    showErrorToast(message, requestId);
                }
            }

            if (success) {
                emitComplete({ requestId, message });
            }

            completingRef.current = false;
        },
        [showErrorToast, showSuccessToast]
    );

    const attachToInflight = useCallback(
        (requestId) => {
            const promise = getInflightDutyCheckPromise();
            if (!promise) return false;

            promise
                .then((res) => {
                    if (res?.status) {
                        finishJob({
                            success: true,
                            message: res.message || "Duty check initiated!",
                            requestId,
                        });
                    } else {
                        finishJob({
                            success: false,
                            message: res?.message || "Failed to initiate duty check",
                            requestId,
                        });
                    }
                })
                .catch(() => {
                    finishJob({
                        success: false,
                        message: "An error occurred. Please try again.",
                        requestId,
                    });
                });

            return true;
        },
        [finishJob]
    );

    const tryRecover = useCallback(
        async (stored) => {
            if (recoveryStarted.current) return;
            recoveryStarted.current = true;

            if (attachToInflight(stored.requestId)) {
                recoveryStarted.current = false;
                return;
            }

            const recovered = await recoverDutyCheckJob(stored.startedAt);
            if (recovered?.success) {
                finishJob({
                    success: true,
                    message: recovered.message,
                    requestId: stored.requestId,
                });
            } else if (isJobStale(stored)) {
                finishJob({
                    success: false,
                    message: "Status check timed out. Please try again.",
                    requestId: stored.requestId,
                });
            } else {
                setJob(stored);
            }

            recoveryStarted.current = false;
        },
        [attachToInflight, finishJob]
    );

    useEffect(() => {
        const stored = loadDutyCheckJob();
        if (!stored || stored.status !== "running") return;
        if (isJobStale(stored)) {
            clearDutyCheckJob();
            return;
        }
        setJob(stored);
        setModalOpen(!stored.minimized);
        tryRecover(stored);
    }, [tryRecover]);

    const ensureModalData = useCallback(async () => {
        try {
            const tasks = [];
            if (departments.length === 0) {
                tasks.push(
                    CommanServices.departments().then((res) => {
                        if (res?.status) setDepartments(res.data || []);
                    })
                );
            }
            if (appUsers.length === 0) {
                tasks.push(
                    CommanServices.getAppUsers().then((res) => {
                        if (res?.status) setAppUsers(res.data || []);
                    })
                );
            }
            await Promise.all(tasks);
        } catch {
            /* ignore */
        }
    }, [appUsers.length, departments.length]);

    const openModal = useCallback(() => {
        setModalOpen(true);
        ensureModalData();
    }, [ensureModalData]);

    const closeModal = useCallback(() => {
        if (job?.status === "running") return;
        setModalOpen(false);
    }, [job?.status]);

    const setMinimized = useCallback((minimized) => {
        setJob((prev) => {
            if (prev?.status !== "running") return prev;
            const next = { ...prev, minimized, modalOpen: !minimized };
            saveDutyCheckJob(next);
            return next;
        });
        if (minimized) {
            setModalOpen(false);
        }
    }, []);

    const expandModal = useCallback(() => {
        setJob((prev) => {
            if (prev?.status !== "running") return prev;
            const next = { ...prev, minimized: false, modalOpen: true };
            saveDutyCheckJob(next);
            return next;
        });
        setModalOpen(true);
        ensureModalData();
    }, [ensureModalData]);

    const startDutyCheck = useCallback(
        async (payload) => {
            if (job?.status === "running" || getInflightDutyCheckPromise()) {
                return { duplicate: true };
            }

            const requestId =
                typeof crypto !== "undefined" && crypto.randomUUID
                    ? crypto.randomUUID()
                    : `dc_${Date.now()}`;

            const runningJob = {
                status: "running",
                minimized: false,
                modalOpen: true,
                startedAt: Date.now(),
                requestId,
            };
            saveDutyCheckJob(runningJob);
            setJob(runningJob);

            try {
                const res = await runDutyCheckRequest(payload);
                if (res?.status) {
                    finishJob({
                        success: true,
                        message: res.message || "Duty check initiated!",
                        requestId,
                    });
                    return { success: true, message: res.message };
                }
                finishJob({
                    success: false,
                    message: res?.message || "Failed to initiate duty check",
                    requestId,
                });
                return { success: false, message: res?.message };
            } catch {
                finishJob({
                    success: false,
                    message: "An error occurred. Please try again.",
                    requestId,
                });
                return { success: false };
            }
        },
        [finishJob, job?.status]
    );

    const isRunning = job?.status === "running";
    const isMinimized = Boolean(isRunning && job?.minimized);

    const value = useMemo(
        () => ({
            job,
            isRunning,
            isMinimized,
            modalOpen,
            departments,
            appUsers,
            openModal,
            closeModal,
            setMinimized,
            expandModal,
            startDutyCheck,
        }),
        [
            appUsers,
            closeModal,
            departments,
            expandModal,
            isMinimized,
            isRunning,
            job,
            modalOpen,
            openModal,
            setMinimized,
            startDutyCheck,
        ]
    );

    return (
        <DutyCheckJobContext.Provider value={value}>
            {children}
        </DutyCheckJobContext.Provider>
    );
}

export function useDutyCheckJob() {
    const ctx = useContext(DutyCheckJobContext);
    if (!ctx) {
        throw new Error("useDutyCheckJob must be used within DutyCheckJobProvider");
    }
    return ctx;
}

export { COMPLETE_EVENT };
